import * as PIXI from "pixi.js";
import { rootEventBus, EnumRootEventBus } from "../../../managers/RootEventBus";
import { cursorEventBus, EnumCursorEventBus } from "../../UI/CustomCursor/CustomCursorEventBus";
import RulerRenderer from "./RulerRenderer";
import Line from "../../Line/Line";
import getAngleBetweenPoints from "../../../utils/math/getAngleBtwPoints";
import { normalizeRadian } from "../../../utils/math/normalizeRadian";
import getCleanCoord from "../../../utils/coord/getCleanCoord";
import getProjection from "../../../utils/coord/getProjection";
import addInteractivity from "../../../utils/addInteractivity";
import { Coord } from "../../../types/Interface";
import { CustomCursorType } from "../../UI/CustomCursor/CustomCursor";
import { areCoordsEqual } from "../../../utils/coord/areCoordsEqual";


const DEFAULT_COORD = { x: 400, y: 480 };


export default class Ruler {
  private RulerRenderer = new RulerRenderer();
  private isFlipped: boolean = false;
  private isGrabbing: boolean = false;
  private isRotating: boolean = false;
  private isRotatingLeft!: boolean;
  private isDrawingLine: boolean = false;
  private mStartCoord!: Coord;
  private grabDelta!: Coord;
  private bounds!: { start: Coord, end: Coord };
  private drawing!: Line;
  private lineColor: number = 0x000000;

  constructor(private getDrawing: (line: Line) => void) {
    this.RulerRenderer.draw();

    Object.assign(this.RulerRenderer, DEFAULT_COORD);
    this.addInteractivities();

    rootEventBus.on(EnumRootEventBus.setPointerMove, (coord: Coord) => this.handlePointerMove(coord));
    rootEventBus.on(EnumRootEventBus.setPointerUp, _ => this.handlePointerUp());
  }
  public setVisible = (v: boolean) => this.RulerRenderer.visible = v;
  public getVisible = () => this.RulerRenderer.visible;

  public setColor = (color: number) => this.lineColor = color;
  private addInteractivities() {
    addInteractivity(this.RulerRenderer.flipButton, { handleDownFunc: _ => this.handleDownFlipButton() }, CustomCursorType.pointer);
    addInteractivity(this.RulerRenderer.grabButton, { handleDownFunc: e => this.handleDownGrabButton(e) }, CustomCursorType.grab);
    addInteractivity(this.RulerRenderer.rotateRightButton, { handleDownFunc: e => this.handleDownRotateButton(e, false) }, CustomCursorType.rotate);
    addInteractivity(this.RulerRenderer.rotateLeftButton, { handleDownFunc: e => this.handleDownRotateButton(e, true) }, CustomCursorType.rotate);
    addInteractivity(
      this.RulerRenderer.drawingArea,
      { handleOverFunc: _ => this.handleOverDrawingArea(false), handleDownFunc: e => this.handleDownDrawingArea(e, false) },
      CustomCursorType.pencil
    );
    addInteractivity(
      this.RulerRenderer.backDrawingArea,
      { handleOverFunc: _ => this.handleOverDrawingArea(true), handleDownFunc: e => this.handleDownDrawingArea(e, true) },
      CustomCursorType.pencil
    )
  }
  private handleOverDrawingArea(isBack: boolean) {
    const rot = this.RulerRenderer.rotation + (isBack ? Math.PI : 0);
    cursorEventBus.emit(EnumCursorEventBus.setRotation, rot);

    this.drawing = new Line({ color: this.lineColor, width: 5, isEqualSymbol: false })
  }
  private handleDownDrawingArea(e: PIXI.FederatedPointerEvent, isBack: boolean) {
    const coord = getCleanCoord(e.global);
    const Coords = this.RulerRenderer.getGraduationBounds(isBack);
    this.isDrawingLine = true;
    this.mStartCoord = getProjection(coord, Coords.start, Coords.end);
    this.bounds = Coords;
    rootEventBus.emit(EnumRootEventBus.setRulerLineStartCoord);
  }
  private handleDownFlipButton() {
    this.RulerRenderer.rotation = normalizeRadian(this.RulerRenderer.rotation + Math.PI);
    this.isFlipped = !this.isFlipped;
  };
  private handleDownRotateButton(e: PIXI.FederatedPointerEvent, isLeft: boolean) {
    this.isRotating = true;
    this.isRotatingLeft = isLeft;
    this.mStartCoord = getCleanCoord(e.global)
    this.handleEventMode(false);
  };
  private handleDownGrabButton(e: PIXI.FederatedPointerEvent) {
    this.isGrabbing = true;
    this.mStartCoord = getCleanCoord(e.global)
    this.grabDelta = getCleanCoord({ x: this.RulerRenderer.x - this.mStartCoord.x, y: this.RulerRenderer.y - this.mStartCoord.y })
    this.handleEventMode(false);
  }
  private handlePointerMove(coord: Coord) {
    if (this.isGrabbing) {
      this.RulerRenderer.x = coord.x + this.grabDelta.x;
      this.RulerRenderer.y = coord.y + this.grabDelta.y;
    } else if (this.isRotating) {
      const rulerCoord = { x: this.RulerRenderer.x, y: this.RulerRenderer.y };
      const rad = getAngleBetweenPoints(coord, rulerCoord);
      this.RulerRenderer.rotation = rad + (this.isFlipped && this.isRotatingLeft ? 0 : this.isRotatingLeft ? 0 : Math.PI);
    } else if (this.isDrawingLine) {
      const projectedPoint = getProjection(coord, this.bounds.start, this.bounds.end);
      this.drawing.draw(this.mStartCoord, projectedPoint);
    }
  }
  private handlePointerUp() {
    this.mStartCoord = undefined;
    this.isGrabbing = false;
    this.isRotating = false;
    this.isDrawingLine = false;
    this.bounds = undefined;
    this.handleEventMode(true);
    if (this.getVisible() && this.drawing && !areCoordsEqual([this.drawing.coordStart], [this.drawing.coordEnd])) {
      this.getDrawing(this.drawing);
      this.drawing = undefined;
    }
  }
  private handleEventMode(v: boolean) {
    [
      this.RulerRenderer.flipButton,
      this.RulerRenderer.grabButton,
      this.RulerRenderer.rotateRightButton,
      this.RulerRenderer.rotateLeftButton
    ]
      .forEach(elm => elm.eventMode = v ? 'static' : 'none');
  }
}